/**
 * HTTP请求工具
 * 基于axios封装的统一请求客户端，集成TanStack Query
 * @author 系统
 * @date 2024
 */

import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { ElMessage } from 'element-plus'
import { getActivePinia } from 'pinia'
import router from '@/router'
let __authLoggingOut = false
import { queryClient } from './query-client'

// 请求响应接口
interface ApiResponse<T = any> {
  code: number
  message: string
  data: T
  success?: boolean
}

/**
 * 创建axios实例
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || '',
    timeout: 10000,
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json'
    }
  })

  // 请求拦截器
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token =
        localStorage.getItem('token') ||
        sessionStorage.getItem('token') ||
        localStorage.getItem('auth_token') ||
        sessionStorage.getItem('auth_token')
      if (token && config.headers) {
        config.headers.set?.('Authorization', `Bearer ${token}`)
      }

      // 添加租户ID
      const tenantId = localStorage.getItem('tenant_id')
      if (tenantId && config.headers) {
        config.headers.set?.('X-Tenant-ID', tenantId)
      }

      // 如果是 FormData，删除 Content-Type 让浏览器自动设置
      if (config.data instanceof FormData && config.headers) {
        config.headers.delete?.('Content-Type')
      }

      return config
    },
    (error) => {
      console.error('请求拦截器错误:', error)
      return Promise.reject(error)
    }
  )

  // 响应拦截器
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      const { data } = response
      if (data && typeof data === 'object' && 'code' in data) {
        if (data.code === 200 || data.code === 0 || data.success) {
          return data.data
        } else {
          ;(ElMessage as any).error({ message: data.message || '请求失败' })
          return Promise.reject(new Error(data.message || '请求失败'))
        }
      }
      const status = response.status
      if (status === 200 || status === 304) {
        return response.data
      }
      return Promise.reject(new Error('请求失败'))
    },
    (error) => {
      console.error('响应拦截器错误:', error)

      // 处理HTTP错误状态码
      if (error.response) {
        const { status, data } = error.response

        switch (status) {
          case 401:
            ;(ElMessage as any).error({ message: '登录已过期，请重新登录' })
            localStorage.removeItem('token')
            localStorage.removeItem('auth_token')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('userInfo')
            sessionStorage.removeItem('token')
            sessionStorage.removeItem('auth_token')
            sessionStorage.removeItem('refreshToken')
            sessionStorage.removeItem('userInfo')
            if (!__authLoggingOut) {
              __authLoggingOut = true
              ;(async () => {
                try {
                  // 检查 Pinia 是否已激活
                  const activePinia = getActivePinia()
                  if (!activePinia) {
                    console.warn('[request] Pinia not active, skipping store logout')
                    // 直接跳转登录页
                    const redirect = encodeURIComponent(window.location.pathname + window.location.search)
                    router.replace({ path: '/login', query: { redirect, cause: '401' } }).catch(() => {})
                    return
                  }
                  
                  const { useUserStore } = await import('@/stores/user')
                  const store = useUserStore()
                  await store.logout(false, true)
                } catch (e) {
                  console.error('[request] Logout failed:', e)
                  // 兜底处理：清除本地存储并跳转
                  localStorage.clear()
                  sessionStorage.clear()
                  const redirect = encodeURIComponent(window.location.pathname + window.location.search)
                  router.replace({ path: '/login', query: { redirect, cause: '401' } }).catch(() => {})
                }
              })()
            }
            {
              const redirect = encodeURIComponent(window.location.pathname + window.location.search)
              router.replace({ path: '/login', query: { redirect, cause: '401' } }).catch(() => {})
            }
            break
          case 403:
            ;(ElMessage as any).error({ message: '权限不足' })
            break
          case 404:
            ;(ElMessage as any).error({ message: '请求的资源不存在' })
            break
          case 500:
            ;(ElMessage as any).error({ message: '服务器内部错误' })
            break
          default:
            ;(ElMessage as any).error({ message: data?.message || `请求失败 (${status})` })
        }
      } else if (error.request) {
        ;(ElMessage as any).error({ message: '网络连接失败，请检查网络' })
      } else {
        ;(ElMessage as any).error({ message: error.message || '请求失败' })
      }

      return Promise.reject(error)
    }
  )

  return instance
}

// 创建请求实例
const request = createAxiosInstance()

/**
 * 封装的请求方法
 */
export const requestMethods = {
  /**
   * GET请求
   */
  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return request.get(url, config)
  },

  /**
   * POST请求
   */
  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return request.post(url, data, config)
  },

  /**
   * PUT请求
   */
  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return request.put(url, data, config)
  },

  /**
   * DELETE请求
   */
  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return request.delete(url, config)
  },

  /**
   * PATCH请求
   */
  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return request.patch(url, data, config)
  }
}

/**
 * TanStack Query 集成工具
 * 提供查询、变更的便捷方法
 */
export const queryUtils = {
  /**
   * 使失效查询缓存
   * @param queryKey 查询键
   */
  invalidateQueries: (queryKey: any[]) => {
    return queryClient.invalidateQueries({ queryKey })
  },

  /**
   * 移除查询缓存
   * @param queryKey 查询键
   */
  removeQueries: (queryKey: any[]) => {
    return queryClient.removeQueries({ queryKey })
  },

  /**
   * 设置查询数据
   * @param queryKey 查询键
   * @param data 数据
   */
  setQueryData: (queryKey: any[], data: any) => {
    return queryClient.setQueryData(queryKey, data)
  },

  /**
   * 获取查询数据
   * @param queryKey 查询键
   */
  getQueryData: (queryKey: any[]) => {
    return queryClient.getQueryData(queryKey)
  },

  /**
   * 预取查询
   * @param options 查询选项
   */
  prefetchQuery: (options: any) => {
    return queryClient.prefetchQuery(options)
  },

  /**
   * 清除所有缓存
   */
  clear: () => {
    return queryClient.clear()
  }
}

/**
 * 查询键工厂
 * 用于生成标准化的查询键
 */
export const queryKeys = {
  // 用户相关
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.users.lists(), { filters }] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.users.details(), id] as const
  },

  // 菜单相关
  menus: {
    all: ['menus'] as const,
    lists: () => [...queryKeys.menus.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.menus.lists(), { filters }] as const,
    tree: () => [...queryKeys.menus.all, 'tree'] as const
  },

  // 权限相关
  permissions: {
    all: ['permissions'] as const,
    lists: () => [...queryKeys.permissions.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.permissions.lists(), { filters }] as const,
    tree: () => [...queryKeys.permissions.all, 'tree'] as const
  },

  // 角色相关
  roles: {
    all: ['roles'] as const,
    lists: () => [...queryKeys.roles.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.roles.lists(), { filters }] as const,
    details: () => [...queryKeys.roles.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.roles.details(), id] as const
  },

  // 租户相关
  tenants: {
    all: ['tenants'] as const,
    lists: () => [...queryKeys.tenants.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.tenants.lists(), { filters }] as const,
    details: () => [...queryKeys.tenants.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.tenants.details(), id] as const,
    apps: (tenantId: string) => [...queryKeys.tenants.detail(tenantId), 'apps'] as const,
    orders: (tenantId: string) => [...queryKeys.tenants.detail(tenantId), 'orders'] as const
  },

  // 控制台相关
  dashboard: {
    all: ['dashboard'] as const,
    stats: () => [...queryKeys.dashboard.all, 'stats'] as const
  },

  // 模块相关
  modules: {
    all: ['modules'] as const,
    configs: () => [...queryKeys.modules.all, 'config'] as const,
    config: (moduleName: string) => [...queryKeys.modules.configs(), moduleName] as const
  }
}

/**
 * 模块级API请求工具
 * 根据环境变量配置决定使用新API还是旧API
 * 开发环境使用相对路径走Vite代理，避免CORS问题
 */

const OLD_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
// 开发环境使用 /newapi 代理前缀，生产环境使用完整URL
const NEW_BASE_URL = import.meta.env.DEV ? '/newapi' : (import.meta.env.VITE_NEW_API_BASE_URL || '')

type ApiVersion = 'new' | 'old'

/**
 * 检查指定模块是否启用新API
 * @param moduleKey 模块标识，如 'auth', 'system', 'aimanagement'
 */
export const isNewApiEnabled = (moduleKey: string): boolean => {
  const key = `VITE_API_SWITCH_${moduleKey.toUpperCase()}`
  const value = (import.meta.env as Record<string, string>)[key]
  return value === 'new'
}

/**
 * 为指定模块创建axios请求实例
 * 根据配置自动选择新/旧API baseURL
 * @param moduleKey 模块标识
 */
export const createModuleRequest = (moduleKey: string) => {
  const baseURL = isNewApiEnabled(moduleKey) ? NEW_BASE_URL : OLD_BASE_URL

  const instance = axios.create({
    baseURL,
    timeout: 10000,
    withCredentials: false,
    headers: { 'Content-Type': 'application/json' }
  })

  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token =
        localStorage.getItem('token') ||
        sessionStorage.getItem('token') ||
        localStorage.getItem('auth_token') ||
        sessionStorage.getItem('auth_token')
      if (token && config.headers) {
        config.headers.set?.('Authorization', `Bearer ${token}`)
      }
      const tenantId = localStorage.getItem('tenant_id')
      if (tenantId && config.headers) {
        config.headers.set?.('X-Tenant-ID', tenantId)
      }
      if (config.data instanceof FormData && config.headers) {
        config.headers.delete?.('Content-Type')
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // 复用主实例的响应拦截器逻辑
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      const { data } = response
      if (data && typeof data === 'object' && 'code' in data) {
        if (data.code === 200 || data.code === 0 || data.success) {
          return data.data
        } else {
          ;(ElMessage as any).error({ message: data.message || '请求失败' })
          return Promise.reject(new Error(data.message || '请求失败'))
        }
      }
      const status = response.status
      if (status === 200 || status === 304) {
        return response.data
      }
      return Promise.reject(new Error('请求失败'))
    },
    (error) => {
      if (error.response) {
        const { status, data } = error.response
        switch (status) {
          case 401:
            ;(ElMessage as any).error({ message: '登录已过期，请重新登录' })
            if (!__authLoggingOut) {
              __authLoggingOut = true
              ;(async () => {
                try {
                  const { useUserStore } = await import('@/stores/user')
                  const store = useUserStore()
                  await store.logout(false, true)
                } catch {}
              })()
            }
            {
              const redirect = encodeURIComponent(window.location.pathname + window.location.search)
              router.replace({ path: '/login', query: { redirect, cause: '401' } }).catch(() => {})
            }
            break
          case 403:
            ;(ElMessage as any).error({ message: '权限不足' })
            break
          case 404:
            ;(ElMessage as any).error({ message: '请求的资源不存在' })
            break
          case 500:
            ;(ElMessage as any).error({ message: '服务器内部错误' })
            break
          default:
            ;(ElMessage as any).error({ message: data?.message || `请求失败 (${status})` })
        }
      } else if (error.request) {
        ;(ElMessage as any).error({ message: '网络连接失败，请检查网络' })
      } else {
        ;(ElMessage as any).error({ message: error.message || '请求失败' })
      }
      return Promise.reject(error)
    }
  )

  return instance
}

/**
 * 按模块发起请求（自动路由到新/旧API）
 * @param moduleKey 模块标识
 * @param config axios配置
 */
export const requestByModule = <T = any>(moduleKey: string, config: AxiosRequestConfig): Promise<T> => {
  const instance = createModuleRequest(moduleKey)
  return instance(config).then((res: any) => res as T)
}

// 导出默认请求实例（兼容现有代码）
export { request, queryClient }
export default request
